import { Fragment, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  ButtonGroup,
  Card,
  Col,
  Dropdown,
  Form,
  Row,
  Table,
} from "react-bootstrap";
import Loading from "../../Loading";
import SummaryPill from "../../components/SummaryPill";
import {
  ApiError,
  refusalSentence,
  useAuthenticatedQuery,
} from "../../lib/api";
import { processFileResponseOrThrow } from "../../lib/file";
import { formatPercent } from "../../lib/numbers";
import { useQueryBenchmark, useQueryBenchmarks } from "../../models/lib";
import ContributionChart from "./ContributionChart";
import {
  BlendPart,
  DRIVERS_PERIODS,
  DriverRow,
  DriversGroupBy,
  DriversPeriodId,
  GROUP_BY_OPTIONS,
  NO_BRIDGEFT_RETURN,
  PerformanceDrivers as PerformanceDriversData,
  allAccountsExcluded,
  blendDescription,
  blendIsValid,
  clientRows,
  csvFileName,
  driverRows,
  driversCsv,
  excludedAccountsNote,
  formatDriversDate,
  helpedAndHurt,
  performanceDriversPath,
  periodLabel,
  signedPercent,
  talkingPoints,
} from "./lib";

export const PERFORMANCE_DRIVERS_ANCHOR = "performance-drivers";

// A missing figure reads "—", never 0.00%: a flat return is a real result.
const percentOrDash = (value: number | null | undefined, digits: number) =>
  value == null ? "—" : formatPercent(value, digits);
const signedOrDash = (value: number | null | undefined) =>
  value == null ? "—" : signedPercent(value);
// The annualized figure of a period longer than 1 year, always to 2 places
// (5.00%, not 5%); nothing when the API sends none (1 year or shorter).
const annualizedSuffix = (value: number | null | undefined) => {
  if (value == null) return "";
  let rounded = Math.round(value * 10000) / 10000;
  if (Object.is(rounded, -0)) rounded = 0;
  const text = rounded.toLocaleString(undefined, {
    style: "percent",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return ` · ${text} annualized`;
};

type AccountOption = { id: number; name: string };

/** The benchmark the section compares against, for this visit only. */
type AppliedBenchmark = {
  /** Set when the blend is a firm benchmark's securities and weights. */
  firm?: { id: number; name: string };
  parts: BlendPart[];
};

/**
 * Why a firm benchmark's securities and weights cannot be sent as the blend
 * of the read (R58), or undefined when they can.
 */
function firmBenchmarkProblem(parts: BlendPart[]) {
  if (blendIsValid(parts)) return undefined;
  if (!parts.length) return "it has no securities";
  const total = parts.reduce((sum, p) => sum + p.weight, 0);
  if (Math.abs(total - 100) >= 1e-9) {
    return `its weights total ${Number(total.toFixed(4))}%, not 100%`;
  }
  return "its securities cannot be used as a blend";
}

const BlendEditor = ({
  initial,
  onApply,
  onCancel,
}: {
  initial: BlendPart[];
  onApply: (parts: BlendPart[]) => void;
  onCancel: () => void;
}) => {
  const [parts, setParts] = useState<BlendPart[]>(
    initial.length
      ? initial
      : [
          { symbol: "", weight: 60 },
          { symbol: "", weight: 40 },
        ],
  );
  const total = parts.reduce((sum, p) => sum + (p.weight || 0), 0);
  // The same tolerance as blendIsValid, so 0.1 + 64.1 + 35.8 reads as 100.
  const totalIs100 = Math.abs(total - 100) < 1e-9;
  const update = (i: number, change: Partial<BlendPart>) =>
    setParts(parts.map((p, j) => (j === i ? { ...p, ...change } : p)));

  return (
    <Card className="mb-3" body>
      <Card.Title as="h5">Benchmark Blend</Card.Title>
      {parts.map((part, i) => (
        <Row
          key={i}
          className="g-2 mb-2 align-items-center"
          style={{ maxWidth: 520 }}
        >
          <Col xs={6}>
            <Form.Control
              aria-label={`Fund ${i + 1} ticker`}
              placeholder="Ticker, e.g. ACWI"
              value={part.symbol}
              onChange={(e) =>
                update(i, { symbol: e.target.value.toUpperCase() })
              }
            />
          </Col>
          <Col xs={4}>
            <Form.Control
              aria-label={`Fund ${i + 1} weight`}
              type="number"
              min={0}
              max={100}
              value={part.weight}
              onChange={(e) => update(i, { weight: Number(e.target.value) })}
            />
          </Col>
          <Col xs={2}>
            <Button
              variant="outline-secondary"
              aria-label={`Remove fund ${i + 1}`}
              disabled={parts.length < 2}
              onClick={() => setParts(parts.filter((_, j) => j !== i))}
            >
              ✕
            </Button>
          </Col>
        </Row>
      ))}
      <div className="d-flex flex-wrap gap-2 align-items-center mb-2">
        <Button
          size="sm"
          variant="outline-primary"
          onClick={() => setParts([...parts, { symbol: "", weight: 0 }])}
        >
          + Add Fund
        </Button>
        <span className={totalIs100 ? "" : "text-danger"}>
          Total: <strong>{Number(total.toFixed(4))}%</strong>
          {totalIs100 ? "" : " (must be 100%)"}
        </span>
      </div>
      <Form.Text className="d-block mb-2">
        Applies for this visit only; the blend is not saved.
      </Form.Text>
      <div className="d-flex gap-2">
        <Button disabled={!blendIsValid(parts)} onClick={() => onApply(parts)}>
          Apply
        </Button>
        <Button variant="outline-secondary" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </Card>
  );
};

/**
 * Performance Drivers (APP-2005): the contributors and detractors behind a
 * household's or an account's return over a period. Advisor View has the
 * Detail table, talking points and CSV export; Client Portal View previews
 * what a client would see: the top three each way, no talking points.
 *
 * Its filters and benchmark belong to one entity: callers key it by `kind:id`
 * so moving to another household or account remounts it with fresh state. The
 * benchmark is a firm benchmark or a blend, and applies for this visit only:
 * nothing is saved, so opening the section again starts with no benchmark.
 */
const PerformanceDrivers = ({
  kind,
  id,
  name,
  accounts = [],
}: {
  kind: "households" | "accounts";
  id: number;
  name: string;
  /** The household's accounts, for the Accounts filter. */
  accounts?: AccountOption[];
}) => {
  const [period, setPeriod] = useState<DriversPeriodId>("ytd");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [groupBy, setGroupBy] = useState<DriversGroupBy>("asset_class");
  const [selected, setSelected] = useState<number[]>([]);
  const [clientView, setClientView] = useState(false);
  const [open, setOpen] = useState<Set<string>>(new Set());
  const [editing, setEditing] = useState(false);
  const [applied, setApplied] = useState<AppliedBenchmark>({ parts: [] });
  const blend = applied.parts;
  const clearBenchmark = () => setApplied({ parts: [] });
  // A firm benchmark is applied once its securities and weights are read
  // (the list sends none); until then the current benchmark stays.
  const [picking, setPicking] = useState<{ id: number; name: string }>();
  const [refused, setRefused] = useState<Map<number, string>>(new Map());
  const [pickProblem, setPickProblem] = useState<string>();
  const firmBenchmarks = useQueryBenchmarks();
  const pickedRead = useQueryBenchmark(picking?.id ?? 0, {
    enabled: picking != null,
  });
  useEffect(() => {
    if (!picking) return;
    if (pickedRead.isError) {
      setPickProblem(`"${picking.name}" could not be read. Try again.`);
      setPicking(undefined);
      return;
    }
    if (pickedRead.data?.id !== picking.id) return;
    const parts = pickedRead.data.primarySecurities.map(
      ({ symbol, weight }) => ({ symbol, weight }),
    );
    const problem = firmBenchmarkProblem(parts);
    if (problem) {
      setRefused((was) => new Map(was).set(picking.id, problem));
      setPickProblem(`"${picking.name}" cannot be used: ${problem}.`);
    } else {
      setApplied({ firm: picking, parts });
      setPickProblem(undefined);
    }
    setPicking(undefined);
  }, [picking, pickedRead.data, pickedRead.isError]);
  const pickFirmBenchmark = (benchmark: { id: number; name: string }) => {
    setEditing(false);
    setPickProblem(undefined);
    setPicking({ id: benchmark.id, name: benchmark.name });
  };
  const [csvError, setCsvError] = useState(false);
  const [copyState, setCopyState] = useState<"idle" | "copied" | "failed">(
    "idle",
  );

  // Only accounts the household still has count, so a selection made before its
  // accounts changed cannot send an ID the API now refuses.
  const accountIdsNow = accounts.map((a) => a.id);
  const chosen = selected.filter((accountId) =>
    accountIdsNow.includes(accountId),
  );
  const allSelected =
    chosen.length === 0 ||
    accountIdsNow.every((accountId) => chosen.includes(accountId));
  const accountIds = allSelected ? undefined : chosen;
  const noAccounts = kind === "households" && accounts.length === 0;
  const customDatesChosen = startDate !== "" && endDate !== "";
  const customReady =
    period !== "custom" || (customDatesChosen && endDate > startDate);

  const path = performanceDriversPath(kind, id, {
    period,
    groupBy,
    startDate,
    endDate,
    accountIds,
    benchmark: blend,
  });
  const {
    data: body,
    isPending,
    isError,
    error,
    refetch,
  } = useAuthenticatedQuery<
    { data: PerformanceDriversData },
    { data: PerformanceDriversData }
  >({
    queryKey: [path],
    urlBuilderFn: () => path,
    queryFn: async (response) =>
      response as unknown as { data: PerformanceDriversData },
    queryOptions: {
      enabled: customReady && !noAccounts,
      // A refusal (a 4xx) says what to change; asking again cannot help.
      retry: (failures, err) =>
        !(err instanceof ApiError && err.status >= 400 && err.status < 500) &&
        failures < 3,
    },
  });
  const data = body?.data;
  const refusal = isError ? refusalSentence(error) : undefined;

  const rows = useMemo(() => (data ? driverRows(data) : []), [data]);
  const scope =
    kind === "accounts" ? "account" : allSelected ? "household" : "accounts";
  const blendName = !blend.length
    ? undefined
    : (applied.firm?.name ?? blendDescription(blend));
  const story =
    data && !clientView ? talkingPoints(data, scope, blendName) : "";
  // A new period or filter means new text, so the button stops saying "Copied".
  useEffect(() => setCopyState("idle"), [story]);

  const scopeLabel =
    kind === "accounts" || allSelected
      ? name
      : accounts
          .filter((a) => chosen.includes(a.id))
          .map((a) => a.name)
          .join(" + ");
  // A total that is not BridgeFT's own return is the accounts' returns chained
  // together (a subset, or a household that reads as one), so it says so.
  const chained = data?.total?.isOfficial === false;
  const totalLabel = chained ? "Chained return" : "Total (time-weighted)";

  const periodAvailable = (pid: DriversPeriodId) =>
    pid === "custom" ||
    data?.periods.find((p) => p.id === pid)?.available !== false;

  const toggleAccount = (accountId: number) => {
    const current = allSelected ? accountIdsNow : chosen;
    const next = current.includes(accountId)
      ? current.filter((x) => x !== accountId)
      : [...current, accountId];
    if (next.length > 0) setSelected(next);
  };

  const downloadCsv = async () => {
    if (!data) return;
    setCsvError(false);
    try {
      await processFileResponseOrThrow(
        new Response(driversCsv(data), {
          headers: {
            "content-type": "text/csv",
            "content-disposition": `attachment; filename=${csvFileName(name, data)}`,
          },
        }),
      );
    } catch {
      setCsvError(true);
    }
  };

  const { top, other } = clientRows(rows);

  return (
    <section
      id={PERFORMANCE_DRIVERS_ANCHOR}
      className="performance-drivers mb-4"
      aria-labelledby="performance-drivers-title"
    >
      <div className="d-flex flex-wrap justify-content-between align-items-start gap-2 mb-3">
        <div>
          <h4 id="performance-drivers-title" className="mb-1">
            Performance Drivers
          </h4>
          {data ? (
            <div className="text-body-secondary small">
              Data as of {formatDriversDate(data.asOfDate)}
            </div>
          ) : null}
        </div>
        <ButtonGroup className="drivers-toggle" aria-label="View">
          <Button
            size="sm"
            variant="outline-secondary"
            active={!clientView}
            aria-pressed={!clientView}
            onClick={() => setClientView(false)}
          >
            Advisor View
          </Button>
          <Button
            size="sm"
            variant="outline-secondary"
            active={clientView}
            aria-pressed={clientView}
            onClick={() => setClientView(true)}
          >
            Client Portal View
          </Button>
        </ButtonGroup>
      </div>
      {clientView ? (
        <Alert variant="light" className="py-2">
          This is what the client would see on the portal: the top three each
          way, no talking points, and the benchmark you chose.
        </Alert>
      ) : null}

      <Row className="g-3 mb-3">
        <Col md={6} xl={3}>
          <Form.Label htmlFor="drivers-period">Period</Form.Label>
          <Form.Select
            id="drivers-period"
            value={period}
            onChange={(e) => setPeriod(e.target.value as DriversPeriodId)}
          >
            {DRIVERS_PERIODS.map((p) => (
              <option key={p.id} value={p.id} disabled={!periodAvailable(p.id)}>
                {p.label}
                {periodAvailable(p.id) ? "" : " (not enough history)"}
              </option>
            ))}
          </Form.Select>
          {period === "custom" ? (
            <Row className="g-2 mt-1">
              <Col>
                <Form.Label htmlFor="drivers-start">Start Date</Form.Label>
                <Form.Control
                  id="drivers-start"
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                />
              </Col>
              <Col>
                <Form.Label htmlFor="drivers-end">End Date</Form.Label>
                <Form.Control
                  id="drivers-end"
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                />
              </Col>
            </Row>
          ) : null}
        </Col>
        {kind === "households" && accounts.length > 1 ? (
          <Col md={6} xl={3}>
            <Form.Label id="drivers-accounts-label">Accounts</Form.Label>
            <Dropdown autoClose="outside">
              <Dropdown.Toggle
                variant="outline-secondary"
                className="w-100 text-start text-truncate"
                aria-labelledby="drivers-accounts-label"
              >
                {allSelected
                  ? `Whole Household (${accounts.length} accounts)`
                  : `${chosen.length} of ${accounts.length} accounts`}
              </Dropdown.Toggle>
              <Dropdown.Menu>
                <div className="px-3 py-1">
                  <Form.Check
                    id="drivers-account-all"
                    label="Whole Household"
                    checked={allSelected}
                    onChange={() => setSelected([])}
                  />
                </div>
                <Dropdown.Divider />
                {accounts.map((a) => (
                  <div key={a.id} className="px-3 py-1">
                    <Form.Check
                      id={`drivers-account-${a.id}`}
                      label={a.name}
                      checked={allSelected || chosen.includes(a.id)}
                      onChange={() => toggleAccount(a.id)}
                    />
                  </div>
                ))}
              </Dropdown.Menu>
            </Dropdown>
          </Col>
        ) : null}
        <Col md={6} xl={3}>
          <Form.Label id="drivers-benchmark-label">Benchmark</Form.Label>
          {clientView ? (
            <div aria-labelledby="drivers-benchmark-label" className="py-2">
              {blendName ?? "None"}
            </div>
          ) : (
            <Dropdown>
              <Dropdown.Toggle
                variant="outline-secondary"
                className="w-100 text-start text-truncate"
                aria-labelledby="drivers-benchmark-label"
              >
                {blendName ?? "None"}
              </Dropdown.Toggle>
              <Dropdown.Menu>
                <Dropdown.Header>Firm benchmarks</Dropdown.Header>
                {firmBenchmarks.isError ? (
                  <Dropdown.ItemText className="text-danger">
                    Failed to load firm benchmarks
                  </Dropdown.ItemText>
                ) : !firmBenchmarks.data ? (
                  <Dropdown.ItemText className="text-body-secondary">
                    Loading…
                  </Dropdown.ItemText>
                ) : firmBenchmarks.data.length === 0 ? (
                  <Dropdown.ItemText className="text-body-secondary">
                    None yet
                  </Dropdown.ItemText>
                ) : (
                  firmBenchmarks.data.map((b) => (
                    <Dropdown.Item
                      key={b.id}
                      active={applied.firm?.id === b.id}
                      disabled={refused.has(b.id) || picking?.id === b.id}
                      title={
                        refused.has(b.id)
                          ? `Cannot be used: ${refused.get(b.id)}`
                          : undefined
                      }
                      onClick={() => pickFirmBenchmark(b)}
                    >
                      {b.name}
                    </Dropdown.Item>
                  ))
                )}
                <Dropdown.Divider />
                <Dropdown.Item onClick={() => setEditing(true)}>
                  {blend.length && !applied.firm
                    ? "Edit this blend…"
                    : "Build a blend…"}
                </Dropdown.Item>
                {blend.length ? (
                  <Dropdown.Item onClick={clearBenchmark}>
                    No benchmark
                  </Dropdown.Item>
                ) : null}
              </Dropdown.Menu>
            </Dropdown>
          )}
        </Col>
        <Col md={6} xl={3}>
          <Form.Label id="drivers-group-label">Group By</Form.Label>
          <div>
            <ButtonGroup
              className="drivers-group-by d-flex"
              aria-labelledby="drivers-group-label"
            >
              {GROUP_BY_OPTIONS.map((g) => (
                <Button
                  key={g.id}
                  variant="outline-primary"
                  active={groupBy === g.id}
                  aria-pressed={groupBy === g.id}
                  onClick={() => {
                    setGroupBy(g.id);
                    setOpen(new Set());
                  }}
                >
                  {g.label}
                </Button>
              ))}
            </ButtonGroup>
          </div>
        </Col>
      </Row>

      {pickProblem && !clientView ? (
        <Alert
          variant="warning"
          dismissible
          onClose={() => setPickProblem(undefined)}
        >
          {pickProblem}
        </Alert>
      ) : null}

      {editing && !clientView ? (
        <BlendEditor
          initial={applied.firm ? [] : blend}
          onCancel={() => setEditing(false)}
          onApply={(parts) => {
            setApplied({ parts });
            setEditing(false);
          }}
        />
      ) : null}

      {noAccounts ? (
        <p className="text-body-secondary">
          This household has no accounts yet, so there are no drivers to show.
        </p>
      ) : !customReady ? (
        <p className="text-body-secondary">
          {customDatesChosen
            ? "The end date must be after the start date."
            : "Choose a start date and an end date."}
        </p>
      ) : isPending ? (
        <Loading message="Performance Drivers" />
      ) : refusal ? (
        // The API said what is wrong: show it, and when the chosen benchmark is
        // the likely cause, offer to drop it rather than a retry that would
        // fail the same way.
        <Alert variant="warning" className="d-flex align-items-center gap-3">
          {refusal}
          {blend.length ? (
            <Button
              size="sm"
              variant="outline-secondary"
              onClick={clearBenchmark}
            >
              Remove benchmark
            </Button>
          ) : null}
        </Alert>
      ) : isError || !data ? (
        <Alert variant="danger" className="d-flex align-items-center gap-3">
          Failed to load Performance Drivers
          <Button
            size="sm"
            variant="outline-danger"
            onClick={() => void refetch()}
          >
            Try again
          </Button>
        </Alert>
      ) : !data.period.available ? (
        <Alert variant="secondary">
          {periodLabel(data.period.id)} is not available for this selection.
          {data.historyStart
            ? ` Its history starts on ${formatDriversDate(data.historyStart)}.`
            : ""}
        </Alert>
      ) : allAccountsExcluded(
          data.excludedAccounts,
          kind === "accounts" ? [id] : allSelected ? accountIdsNow : chosen,
        ) ? (
        <Alert variant="secondary">
          Performance Drivers is not available for this selection.{" "}
          {excludedAccountsNote(data.excludedAccounts)}
        </Alert>
      ) : !data.total ? (
        <Alert variant="secondary">
          {[NO_BRIDGEFT_RETURN, excludedAccountsNote(data.excludedAccounts)]
            .filter(Boolean)
            .join(" ")}
        </Alert>
      ) : (
        <>
          {excludedAccountsNote(data.excludedAccounts) ? (
            <p className="text-muted small mb-2">
              {excludedAccountsNote(data.excludedAccounts)}
            </p>
          ) : null}
          <div className="d-flex flex-wrap gap-3 mb-3 align-items-stretch">
            <SummaryPill
              size="sm"
              label={`${scopeLabel}${chained ? " · Chained return" : ""}${annualizedSuffix(data.total.annualizedReturn)}`}
              value={percentOrDash(data.total.return, 2)}
            />
            {data.benchmark?.unavailable ? (
              <SummaryPill size="sm" label="Benchmark unavailable" value="—" />
            ) : data.benchmark?.return != null ? (
              <>
                <SummaryPill
                  size="sm"
                  label={`${blendName ?? "Benchmark"}${annualizedSuffix(data.benchmark.annualizedReturn)}`}
                  value={formatPercent(data.benchmark.return, 2)}
                />
                <SummaryPill
                  size="sm"
                  label="Difference"
                  value={signedOrDash(data.benchmark.difference)}
                />
              </>
            ) : (
              <SummaryPill
                size="sm"
                label={
                  data.period.hasBenchmark
                    ? "No benchmark chosen"
                    : `No benchmark for ${periodLabel(data.period.id)}`
                }
                value="—"
              />
            )}
            {story ? (
              <Card className="drivers-talking-points flex-grow-1">
                <Card.Body>
                  <div className="d-flex justify-content-between align-items-center gap-2 mb-2">
                    <strong>Talking Points</strong>
                    <Button
                      size="sm"
                      variant="outline-primary"
                      onClick={() => {
                        // The clipboard is missing on an insecure page and can be
                        // refused; say so rather than fail silently.
                        const copy = navigator.clipboard?.writeText(story);
                        if (!copy) {
                          setCopyState("failed");
                          return;
                        }
                        copy.then(
                          () => setCopyState("copied"),
                          () => setCopyState("failed"),
                        );
                      }}
                    >
                      {copyState === "copied"
                        ? "Copied"
                        : copyState === "failed"
                          ? "Copy failed: select the text"
                          : "Copy Talking Points"}
                    </Button>
                  </div>
                  <Card.Text>{story}</Card.Text>
                </Card.Body>
              </Card>
            ) : null}
          </div>

          <Row className="g-4">
            <Col xxl={6}>
              <h5>Contribution to Return</h5>
              <div className="drivers-legend mb-2">
                <span>
                  <i style={{ background: "var(--drivers-pos)" }} />
                  Added to return
                </span>
                <span>
                  <i style={{ background: "var(--drivers-neg)" }} />
                  Reduced return
                </span>
                <span>
                  <i style={{ background: "var(--drivers-neu)" }} />
                  Fees &amp; timing
                </span>
              </div>
              <ContributionChart
                rows={clientView ? top : rows}
                other={clientView ? other : []}
                fees={data.total.feesAndTiming}
                total={data.total.return}
              />
            </Col>
            <Col xxl={6}>
              {clientView ? (
                <ClientGlance rows={rows} />
              ) : (
                <>
                  <div className="d-flex justify-content-between align-items-center gap-2 mb-2">
                    <h5 className="mb-0">Detail</h5>
                    <Button
                      size="sm"
                      variant="outline-primary"
                      onClick={() => void downloadCsv()}
                    >
                      Export CSV
                    </Button>
                  </div>
                  {csvError ? (
                    <Alert variant="danger" className="py-2">
                      The CSV could not be downloaded. Try again.
                    </Alert>
                  ) : null}
                  <DetailTable
                    rows={rows}
                    groupBy={data.groupBy}
                    open={open}
                    setOpen={setOpen}
                    fees={data.total.feesAndTiming}
                    total={data.total.return}
                    totalLabel={totalLabel}
                  />
                  <p className="text-body-secondary small">
                    Avg Weight is the average of each month&apos;s weight. Every
                    month adds up to 100%, and a sold security counts as 0%
                    after its sale, so the column adds up to 100% too.
                  </p>
                </>
              )}
            </Col>
          </Row>
          <p className="text-body-secondary small mt-3">
            Returns for {formatDriversDate(data.period.start)} –{" "}
            {formatDriversDate(data.period.end)}, net of fees. Contributions add
            up to the total return. Past performance does not guarantee future
            results.
          </p>
        </>
      )}
    </section>
  );
};

const ClientGlance = ({ rows }: { rows: DriverRow[] }) => {
  const { helped, hurt } = helpedAndHurt(rows, 3);
  const list = (items: DriverRow[]) =>
    items.length ? (
      <Table size="sm" className="mb-0">
        <tbody>
          {items.map((r) => (
            <tr key={r.key}>
              <td>{r.detail || r.name}</td>
              <td className="text-end fw-bold tabular-nums">
                {signedPercent(r.contribution)}
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
    ) : (
      <p className="text-body-secondary">None</p>
    );
  return (
    <>
      <h5>At a Glance</h5>
      <Row className="g-3">
        <Col sm={6}>
          <strong>Helped Most</strong>
          {list(helped)}
        </Col>
        <Col sm={6}>
          <strong>Held Back Most</strong>
          {list(hurt)}
        </Col>
      </Row>
    </>
  );
};

const DetailTable = ({
  rows,
  groupBy,
  open,
  setOpen,
  fees,
  total,
  totalLabel,
}: {
  rows: DriverRow[];
  groupBy: DriversGroupBy;
  open: Set<string>;
  setOpen: (open: Set<string>) => void;
  fees: number | null;
  total: number | null;
  totalLabel: string;
}) => {
  const heading =
    groupBy === "position"
      ? "Position"
      : groupBy === "category"
        ? "Category"
        : "Asset Class";
  const toggle = (key: string) => {
    const next = new Set(open);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    setOpen(next);
  };
  const mini = (w: number | null, r: number | null) => (
    <div className="drivers-mini">
      {percentOrDash(w, 1)} avg weight · {r == null ? "—" : formatPercent(r, 2)}{" "}
      return
    </div>
  );
  const sold = (soldBy?: string | null) =>
    soldBy ? (
      <span className="badge text-bg-secondary drivers-sold">
        Sold {formatDriversDate(soldBy)}
      </span>
    ) : null;

  return (
    <Table className="drivers-table">
      <thead>
        <tr>
          <th>{heading}</th>
          <th className="text-end drivers-wide">Avg Weight</th>
          <th className="text-end drivers-wide">Return</th>
          <th className="text-end">Contribution</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => {
          const expandable = !!row.positions?.length;
          const isOpen = open.has(row.key);
          return (
            <Fragment key={row.key}>
              <tr
                {...(expandable
                  ? {
                      role: "button",
                      tabIndex: 0,
                      "aria-expanded": isOpen,
                      onClick: () => toggle(row.key),
                      onKeyDown: (e: React.KeyboardEvent) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          toggle(row.key);
                        }
                      },
                    }
                  : {})}
              >
                <td>
                  {expandable ? (isOpen ? "▼ " : "▶ ") : ""}
                  {groupBy === "position" && row.detail ? (
                    <>
                      <span className="text-body-secondary me-1">
                        {row.name}
                      </span>
                      {row.detail}
                    </>
                  ) : (
                    row.name
                  )}
                  {sold(row.soldBy)}
                  {mini(row.avgWeight, row.return)}
                </td>
                <td className="text-end tabular-nums drivers-wide">
                  {percentOrDash(row.avgWeight, 1)}
                </td>
                <td className="text-end tabular-nums drivers-wide">
                  {row.return == null ? "—" : formatPercent(row.return, 2)}
                </td>
                <td className="text-end tabular-nums">
                  {signedPercent(row.contribution)}
                </td>
              </tr>
              {isOpen
                ? row.positions?.map((p) => (
                    <tr
                      key={`${row.key}:${p.key}`}
                      className="text-body-secondary"
                    >
                      <td className="ps-4">
                        <span className="me-1">{p.symbol}</span>
                        {p.description}
                        {sold(p.soldBy)}
                        {mini(p.avgWeight, p.return)}
                      </td>
                      <td className="text-end tabular-nums drivers-wide">
                        {percentOrDash(p.avgWeight, 1)}
                      </td>
                      <td className="text-end tabular-nums drivers-wide">
                        {p.return == null ? "—" : formatPercent(p.return, 2)}
                      </td>
                      <td className="text-end tabular-nums">
                        {signedOrDash(p.contribution)}
                      </td>
                    </tr>
                  ))
                : null}
            </Fragment>
          );
        })}
        <tr className="text-body-secondary">
          <td>Fees &amp; cash-flow timing</td>
          <td className="drivers-wide" />
          <td className="drivers-wide" />
          <td className="text-end tabular-nums">{signedOrDash(fees)}</td>
        </tr>
        <tr className="fw-bold">
          <td>
            {totalLabel}
            <div className="drivers-mini">
              100.0% avg weight ·{" "}
              {total == null ? "—" : formatPercent(total, 2)} return
            </div>
          </td>
          <td className="text-end tabular-nums drivers-wide">100.0%</td>
          <td className="text-end tabular-nums drivers-wide">
            {total == null ? "—" : formatPercent(total, 2)}
          </td>
          <td className="text-end tabular-nums">{signedOrDash(total)}</td>
        </tr>
      </tbody>
    </Table>
  );
};

export default PerformanceDrivers;
